/**
 * React context that hands an ApiClient to every component that needs to
 * talk to the backend. Built on top of Clerk's useAuth hook for token access.
 */

import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiClient } from "./api-client.js";
import { clearTenantBrowserTransientState } from "./account-browser-state.js";

const ApiClientContext = createContext<ApiClient | null>(null);

export function ApiClientProvider({ children }: { children: ReactNode }): React.ReactElement {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(
    () =>
      new ApiClient({
        getToken: async (options) => (await getToken(options)) ?? null,
        // Expired session mid-page (e.g. mid costs-chat): one full reload lets
        // Clerk re-evaluate — a live session resumes with a fresh token (all
        // onboarding state is server-persisted); a dead one hits the normal
        // RedirectToSignIn. Rate-limited so a genuinely broken token can't
        // reload-loop the tab.
        onAuthError: () => {
          try {
            const key = "ccd.authErrorReloadAt";
            const last = Number(window.sessionStorage.getItem(key) ?? 0);
            if (Date.now() - last < 60_000) return;
            window.sessionStorage.setItem(key, String(Date.now()));
            window.location.reload();
          } catch { /* best-effort */ }
        },
        onAccountContextChanged: () => {
          // The ApiClient freezes before this callback. Clear every old-account
          // cache/query input, then let the binding handshake start clean.
          queryClient.clear();
          clearTenantBrowserTransientState();
          window.location.reload();
        },
      }),
    [getToken, queryClient],
  );
  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

/**
 * Public-demo client: no Clerk token, just the `X-Demo-Mode` marker. The API
 * serves the read-only Vital Roots demo account for it. Overrides the normal
 * ApiClient context for the /demo subtree.
 */
export function DemoApiClientProvider({ children }: { children: ReactNode }): React.ReactElement {
  const client = useMemo(
    () =>
      new ApiClient({
        getToken: async () => null,
        extraHeaders: { "X-Demo-Mode": "vital-roots" },
      }),
    [],
  );
  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (!client) {
    throw new Error("useApiClient must be used inside <ApiClientProvider>");
  }
  return client;
}
